import { FunctionComponent, useEffect, useState } from 'react';
import { Box } from '@chakra-ui/react';
import { AnimatePresence, motion } from 'framer-motion';

import Button from '../../../../../../components/_core/Button';
import SunshineIcon from '../../../../../../components/_core/SunshineIcon';
import locales from './locales';

interface ResendBeneficiaryInvitationButtonProps {
  onClick: () => void;
  canResend: boolean;
  isSending: boolean;
}
enum ResendBeneficiaryInvitationButtonState {
  Sent = 'pristine',
  Sending = 'sending',
  Resend = 'resend',
}

const variants = {
  clicked: {
    opacity: 1,
    transition: { duration: 0.3, ease: 'easeOut' },
    y: 0,
  },
  idle: () => ({
    opacity: 0,
    transition: { duration: 0.3, ease: 'easeOut' },
    y: 10,
  }),
};

const getButtonContent = (state: ResendBeneficiaryInvitationButtonState) => {
  switch (state) {
    case ResendBeneficiaryInvitationButtonState.Sent: {
      return (
        <Box alignItems="center" display="flex">
          {locales.sent}
          <SunshineIcon marginLeft="space-8" name="tick" />
        </Box>
      );
    }
    case ResendBeneficiaryInvitationButtonState.Sending: {
      return locales.sending;
    }
    case ResendBeneficiaryInvitationButtonState.Resend:
    default: {
      return locales.resend;
    }
  }
};

const ResendBeneficiaryInvitationButton: FunctionComponent<
  ResendBeneficiaryInvitationButtonProps
> = ({ canResend, isSending, onClick }) => {
  const [buttonState, setButtonState] =
    useState<ResendBeneficiaryInvitationButtonState>(
      canResend
        ? ResendBeneficiaryInvitationButtonState.Resend
        : ResendBeneficiaryInvitationButtonState.Sent,
    );

  useEffect(() => {
    if (isSending) {
      setButtonState(ResendBeneficiaryInvitationButtonState.Sending);
    } else if (
      !isSending &&
      buttonState === ResendBeneficiaryInvitationButtonState.Sending
    ) {
      setButtonState(
        canResend
          ? ResendBeneficiaryInvitationButtonState.Resend
          : ResendBeneficiaryInvitationButtonState.Sent,
      );
    }
  }, [isSending, buttonState, canResend]);

  return (
    <Button
      isDisabled={buttonState !== ResendBeneficiaryInvitationButtonState.Resend}
      onClick={onClick}
      variant="inline-secondary"
    >
      <Box height="100%" overflow="hidden" width="100%">
        <AnimatePresence initial={false} mode="wait">
          <motion.div
            animate="clicked"
            exit="idle"
            initial="idle"
            key={
              buttonState === ResendBeneficiaryInvitationButtonState.Sending
                ? 'clicked'
                : 'idle'
            }
            variants={variants}
          >
            {getButtonContent(buttonState)}
          </motion.div>
        </AnimatePresence>
      </Box>
    </Button>
  );
};

export default ResendBeneficiaryInvitationButton;
